import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { Button } from 'src/components';

import { LanguageDropdown } from '../languageDropdown/LanguageDropdown';

import './header.scss';
import { ROUTE } from 'src/constants/routes';
import { EnterIcon, LogoImage } from 'src/assets';
import classNames from 'classnames';

const LinkMenu = (props) => {
  const { withMenu = false, to, onClick, title, routes = [] } = props;

  const { i18n } = useTranslation();

  const [isMenu, setIsMenu] = useState(false);

  const onMouseEnter = useCallback(() => {
    setIsMenu(true);
  }, []);

  const onMouseLeave = useCallback(() => {
    setIsMenu(false);
  }, []);

  const menuStyles = classNames('header-link__menu', {
    'header-link__menu--active': isMenu && withMenu,
  });

  return (
    <>
      <Link
        to={to?.replace(':language', i18n.language)}
        className="header-link"
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {title}

        <div className={menuStyles}>
          {routes?.map((item) => (
            <Link
              to={item?.to?.replace(':language', i18n.language)}
              className="header-link"
              onClick={onMouseLeave}
            >
              {item.title}
            </Link>
          ))}
        </div>
      </Link>
      <div className="routes--mobile">
        {routes?.map((item) => (
          <Link
            to={item?.to?.replace(':language', i18n.language)}
            className="header-link"
            onClick={onMouseLeave}
          >
            {item.title}
          </Link>
        ))}
      </div>
    </>
  );
};

export const Header = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [color, setColor] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const routes = [
    ROUTE.AUTH.SIGNIN?.replace(':language', i18n.language),
    ROUTE.AUTH.SIGNUP?.replace(':language', i18n.language),
    ROUTE.AUTH.FORGOT_PASSWORD?.replace(':language', i18n.language),
    ROUTE.AUTH.RESET_PASSWORD?.replace(':language', i18n.language),
    ROUTE.AUTH.RESET_PASSWORD_SUCCESS?.replace(':language', i18n.language),
    ROUTE.WITHDRAWAL.CREATED?.replace(':language', i18n.language),
    ROUTE.WITHDRAWAL.SUCCESS?.replace(':language', i18n.language),
    ROUTE.WITHDRAWAL.ERROR?.replace(':language', i18n.language),
    ROUTE.CONTACTS?.replace(':language', i18n.language),
    ROUTE.ABOUT_COMPANY?.replace(':language', i18n.language),
  ];

  const isWhiteHeader = routes.includes(pathname);

  const onChangeHeaderColor = useCallback(() => {
    if (!isWhiteHeader) {
      if (window.scrollY >= 50) {
        setColor('white');
      } else {
        setColor('');
      }
    }
  }, [isWhiteHeader]);

  useEffect(() => {
    window.addEventListener('scroll', onChangeHeaderColor);
    return () => window.removeEventListener('scroll', onChangeHeaderColor);
  }, [onChangeHeaderColor]);

  useEffect(() => {
    if (isWhiteHeader || isMenuOpen) {
      setColor('white');
    } else {
      setColor('');
    }
  }, [isWhiteHeader, isMenuOpen]);

  const onCloseMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  const renderLinkItem = useCallback(
    (title, to, list = []) => {
      const isMenu = list.length > 0;

      return (
        <LinkMenu
          to={to?.replace(':language', i18n.language)}
          onClick={onCloseMenu}
          title={title}
          withMenu={isMenu}
          routes={list}
        />
      );
    },
    [i18n.language, onCloseMenu],
  );

  const onClickButton = useCallback(() => {
    navigate(ROUTE.AUTH.SIGNIN?.replace(':language', i18n.language));
    setIsMenuOpen(false);
  }, [i18n.language, navigate]);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  return (
    <header className="header" style={{ backgroundColor: color }}>
      <div className="header-content">
        <div className="header-left">
          <Link to={ROUTE.HOME}>
            <img src={LogoImage} alt={LogoImage} />
          </Link>
        </div>
        <button className={`burger-menu ${isMenuOpen ? 'open' : ''}`} onClick={toggleMenu}>
          <span />
          <span />
          <span />
        </button>
        <div className={`header-center ${isMenuOpen ? 'open' : ''}`}>
          {renderLinkItem(t('header:markets'), ROUTE.MARKETS.MARKETS)}
          {renderLinkItem(t('header:platform'), ROUTE.PLATFORM, [
            { to: ROUTE.MARKETS.TRADING_SESSION, title: t('header:tradingSessions') },
            { to: ROUTE.MARKETS.INSTRUMENTS, title: t('header:instruments') },
          ])}
          {renderLinkItem(t('header:aboutCompany'), ROUTE.ABOUT_COMPANY)}
          {renderLinkItem(t('header:contacts'), ROUTE.CONTACTS)}
        </div>
        <div className={`header-right ${isMenuOpen ? 'open' : ''}`}>
          {renderLinkItem(t('header:markets'), ROUTE.MARKETS.MARKETS)}
          {renderLinkItem(t('header:platform'), ROUTE.PLATFORM, [
            { to: ROUTE.MARKETS.TRADING_SESSION, title: t('header:tradingSessions') },
            { to: ROUTE.MARKETS.INSTRUMENTS, title: t('header:instruments') },
          ])}
          {renderLinkItem(t('header:aboutCompany'), ROUTE.ABOUT_COMPANY)}
          {renderLinkItem(t('header:contacts'), ROUTE.CONTACTS)}
          <div className="header-right-content">
            <LanguageDropdown />
            <Button title={t('signin:signin')} size="l" icon={EnterIcon} onClick={onClickButton} />
          </div>
        </div>
      </div>
    </header>
  );
};
